import {
  AspectRatio,
  Box,
  Stack,
  Text,
  Link,
  Flex,
  FlexProps,
  HStack,
  useTheme,
  useToast,
  useBoolean,
  Checkbox,
  IconButton,
  Grid,
  Button,
  GridItem,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  useDisclosure,
} from '@chakra-ui/react';
import React, {
  useState,
  ReactNode,
  CSSProperties,
  useContext,
  memo,
  useMemo,
  useCallback,
} from 'react';
import {
  PriceTag,
  ImageWithFallback,
  CategoryAttributesDisplay,
  ImageBanner,
} from 'src/components/atoms';
import {
  Category,
  RootCategory,
  Listing,
  ListingSelected,
  BasicCategory,
  CategoryPreview,
} from 'src/api/v1-api';
import NextLink from 'next/link';
import { apiRequest, handleNextRevalidation } from 'src/utils/fetchUtils';
import { categoryTypeHasPrice } from 'src/utils/categoryUtils';
import { formatDateTime, formatPrice } from 'src/utils/common';
import AuctionTimeLeft from 'src/components/atoms/auction-time-left';
import { MdMap, MdOutlineTimer } from 'react-icons/md';
import { get_location_from_listing } from 'src/utils/attributeUtils';
import { calculateTimeSince } from 'src/utils/timeUtils';
import { useRouter } from 'next/router';
import { useAuth } from 'src/utils/auth';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { ChevronDownIcon, EditIcon } from '@chakra-ui/icons';
import { FaChevronDown } from 'react-icons/fa';
import DenyMessageModal from './deny-message-modal';
import { FiEdit, FiTrash } from 'react-icons/fi';
import {
  determineAuctionInfo,
  filterHiddenAttributes,
  getListingImage,
  getSoldOutMessage,
  markListingAsBroken,
} from 'src/utils/listingUtils';
import { CRAIGSLIST, FACEBOOK, GOOGLE, OTHER, YELP } from 'src/constants/marketplace';
import { capitalize } from 'lodash';
import { LISTING_SELECTION_LIMIT } from 'src/components/organisms/listings-grid';
import { isSampleListing } from 'src/utils/marketplaceUtils';
import { getMarketplaceLink } from 'src/utils/domainUtils';

interface Props {
  listing: Listing;
  category?: BasicCategory | Category | RootCategory | CategoryPreview | null;
  actionButtons?: string;
  removeListing?: (listingId: number) => void;
  loading: boolean;
  listingSelected?: ListingSelected | undefined;
  handleChangeSelected?: (listingId: number, checked: boolean) => void;
  useNextLink?: boolean;
  onCategoryEditClick?: (listingId: number | undefined, categoryId: number | undefined) => void;
  flexProps?: FlexProps;
  columns?: number;
  isSelectionEnabled?: boolean;
  onPageEmpty?: () => void;
}

interface DynamicLinkProps {
  href: string;
  useNextLink: boolean;
  style?: CSSProperties;
  children: ReactNode;
}

const ListingCard = ({
  listing,
  category,
  actionButtons,
  removeListing,
  loading,
  listingSelected,
  handleChangeSelected,
  useNextLink = true,
  onCategoryEditClick,
  flexProps,
  columns,
  isSelectionEnabled = true,
  onPageEmpty,
}: Props): JSX.Element => {
  const router = useRouter();
  const isAdminRoute = router.pathname.startsWith('/slug_admin');
  const toast = useToast();
  const { getToken, hasStaff } = useAuth();
  const { marketplace, hostname, isAffiliate } = useContext(MarketplaceContext);
  const isLowTier =
    !marketplace ||
    !marketplace.tier ||
    marketplace.tier.position === 1 ||
    marketplace.tier.position === 2;
  const isStaffUser = hasStaff();
  const showSearchQueries = isStaffUser || (isAffiliate === false && !isLowTier);
  const marketplaceLink = isAdminRoute && marketplace ? getMarketplaceLink(marketplace) : '/';

  const [showDenyMessageModal, setShowDenyMessageModal] = useBoolean(false);
  const auctionInfo = listing.auction_info ? determineAuctionInfo(listing.auction_info) : undefined;
  const missingAuctionDates = !auctionInfo?.start || !auctionInfo?.end;
  const theme = useTheme();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [imageError, setImageError] = useState(false);

  const featuredImage = getListingImage(listing?.listing_images) ?? '';
  const thumbnailImage = listing?.listing_images?.[0]?.thumbnail ?? '';
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isApproving, setIsApproving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDenying, setIsDenying] = React.useState(false);
  const [cardSelected, setCardSelected] = useState(listingSelected?.isSelected ?? false);

  const handleDelete = async (listingId: number) => {
    const confirmed = confirm('Are you sure you want to delete this listing?');
    if (!confirmed) return;

    setIsDeleting(true);
    const token = await getToken();
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/delete/`;
    try {
      await apiRequest('DELETE', url, token);

      try {
        await handleNextRevalidation(`/_sites/${hostname}/listing/${listingId}`);
      } catch (error) {
        console.error(error);
      }

      toast({
        title: 'Listing deleted',
        description: 'Your listing has been deleted.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      if (removeListing) removeListing(listingId);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to delete listing.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApprove = async (listingId: number) => {
    const token = await getToken();
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${marketplace?.id}/bulk-approve/`;
    try {
      setIsApproving(true);
      await apiRequest('POST', url, token, { listing_ids: [listingId], action: 'approve' });

      try {
        if (!isAdminRoute) {
          await handleNextRevalidation(`/_sites/${hostname}/listing/${listingId}`);
        }
      } catch (error) {
        console.error(error);
      }
      setIsApproving(false);
      toast({
        title: 'Listing approved',
        description: 'The listing will now be visible',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      if (removeListing) {
        removeListing(listingId);
        // Check if we need to trigger page navigation
        if (onPageEmpty) {
          onPageEmpty();
        }
      } else {
        router.reload();
      }
    } catch (error) {
      setIsApproving(false);
      toast({
        title: 'Error',
        description: 'Unable to approve listing.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeny = async (listingId: number, rejectMessage = '') => {
    const token = await getToken();
    let url = '';
    let body = {};
    if (rejectMessage) {
      url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/${marketplace?.id}/deny/`;
      body = { reject_message: rejectMessage };
    } else {
      url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${marketplace?.id}/bulk-deny/`;
      body = { listing_ids: [listingId], action: 'deny' };
    }
    try {
      setIsDenying(true);
      if (rejectMessage) {
        await apiRequest('POST', url, token, body);
      } else {
        await apiRequest('POST', url, token, { listing_ids: [listingId], action: 'deny' });
      }

      try {
        if (!isAdminRoute) {
          await handleNextRevalidation(`/_sites/${hostname}/listing/${listingId}`);
        }
      } catch (error) {
        console.error(error);
      }

      setIsDenying(false);
      toast({
        title: 'Listing denied',
        description: 'The listing will remain hidden.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      if (removeListing) {
        removeListing(listing.id);
        // Check if we need to trigger page navigation
        if (onPageEmpty) {
          onPageEmpty();
        }
      } else {
        router.reload();
      }
    } catch (error) {
      setIsDenying(false);
      toast({
        title: 'Error',
        description: 'Unable to deny listing, it will remain pending.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const DynamicLink = useMemo<React.FC<DynamicLinkProps>>(() => {
    const Comp: React.FC<DynamicLinkProps> = ({ href, useNextLink: useNext, style, children }) => {
      if (useNext) {
        return (
          <NextLink href={href} style={style}>
            {children}
          </NextLink>
        );
      }
      return (
        <Link href={href} style={style}>
          {children}
        </Link>
      );
    };
    Comp.displayName = 'DynamicLink';
    return Comp;
  }, []);

  const LinkWrapper = useMemo<React.FC<{ children: ReactNode }>>(() => {
    const Comp: React.FC<{ children: ReactNode }> = ({ children }) => (
      <DynamicLink
        href={`/listing/${listing.id}`}
        useNextLink={useNextLink}
        style={{ display: 'flex', flexGrow: 1 }}
      >
        {children}
      </DynamicLink>
    );
    Comp.displayName = 'LinkWrapper';
    return Comp;
  }, [listing.id, useNextLink, DynamicLink]);

  const BoxWrapper = useMemo(() => {
    const Comp: React.FC<{ children: ReactNode }> = ({ children }) => (
      <Box style={{ display: 'flex', flexGrow: 1 }}>{children}</Box>
    );
    Comp.displayName = 'BoxWrapper';
    return Comp;
  }, []);

  const CardWrapper = useMemo(() => {
    const Comp: React.FC<{ children: ReactNode }> = ({ children }) =>
      actionButtons && actionButtons === 'pendingListings' ? (
        <BoxWrapper>{children}</BoxWrapper>
      ) : (
        <LinkWrapper>{children}</LinkWrapper>
      );
    Comp.displayName = 'CardWrapper';
    return Comp;
  }, [actionButtons, BoxWrapper, LinkWrapper]);

  const categoryEditClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      onCategoryEditClick && onCategoryEditClick(listing?.id, listing?.category_id);
    },
    [onCategoryEditClick, listing?.id, listing?.category_id],
  );

  const location = get_location_from_listing(listing);

  let timeSinceListed;
  if (listing?.created_at) {
    timeSinceListed = calculateTimeSince(listing?.listed_at ?? listing.created_at);
  }

  const handleCheckBoxChanged = useCallback(() => {
    if (!cardSelected && !isSelectionEnabled) {
      return onOpen();
    }
    setCardSelected((prev) => !prev);
    handleChangeSelected && handleChangeSelected(listing.id, !listingSelected?.isSelected);
  }, [
    cardSelected,
    isSelectionEnabled,
    onOpen,
    handleChangeSelected,
    listing.id,
    listingSelected?.isSelected,
  ]);

  const onImageErrorHandler = useCallback(
    (_event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // Expire candidate/pending non-Loma listings that have broken image links
      if (
        listing?.id &&
        listing?.source &&
        listing.source.toLowerCase() !== 'loma' &&
        (listing?.status === 'candidate' || listing?.status === 'pending')
      ) {
        markListingAsBroken(listing.id);
        setImageError(true);
      }
    },
    [listing?.id, listing?.source, listing?.status],
  );

  if (imageError) {
    return <></>;
  }

  return (
    <Flex
      direction="column"
      overflow="visible"
      position="relative"
      {...flexProps}
      data-test="listing-card"
      data-test-id={`${listing.id}`}
    >
      {actionButtons && actionButtons === 'pendingListings' && (
        <Popover isOpen={isOpen} onClose={onClose} isLazy>
          <PopoverTrigger>
            <Checkbox
              bgColor="white"
              borderColor="gray.700"
              zIndex={2}
              position={'absolute'}
              top={'0.4rem'}
              right={'0.4rem'}
              isDisabled={loading}
              isChecked={cardSelected}
              cursor={'default'}
              size="lg"
              onChange={handleCheckBoxChanged}
              sx={{
                '& .chakra-checkbox__control': {
                  borderRadius: '0 !important',
                },
                '& .chakra-checkbox__control[data-checked]': {
                  borderRadius: '0 !important',
                },
              }}
            ></Checkbox>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody>
              {`Maximum of ${LISTING_SELECTION_LIMIT} listings can be selected. Please take a bulk action before selecting more listings.`}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      )}
      <CardWrapper>
        <Stack
          spacing={{ base: '4', md: '5' }}
          justifyContent="space-between"
          style={{ flexGrow: 1, minWidth: 0 }}
        >
          <Box
            position="relative"
            onClick={
              actionButtons === 'pendingListings'
                ? () => {
                    setCardSelected(!cardSelected);
                    handleChangeSelected &&
                      handleChangeSelected(listing.id, !listingSelected?.isSelected);
                  }
                : undefined
            }
          >
            <AspectRatio ratio={8 / 7}>
              <Box
                borderRadius={{ base: theme.border_radius.border_radius_1 }}
                overflow="hidden"
                position="relative"
              >
                {isSampleListing(marketplace ?? null, listing?.source ?? '') ? (
                  <ImageBanner text="Sample Listing" bgColor="#718096" size="sm" />
                ) : (
                  category?.functional?.show_third_party_ribbons &&
                  [CRAIGSLIST, FACEBOOK, GOOGLE, YELP, OTHER].includes(listing?.source ?? '') && (
                    <ImageBanner
                      text={`Spotted on ${
                        listing?.source === OTHER ? 'the Web' : capitalize(listing?.source)
                      }`}
                      bgColor="#718096"
                      size="sm"
                    />
                  )
                )}
                {thumbnailImage ? (
                  <ImageWithFallback
                    key={`${listing.id}_thumb`}
                    src={thumbnailImage}
                    alt={listing?.title ?? 'Listing Image'}
                    onError={onImageErrorHandler}
                  />
                ) : (
                  <ImageWithFallback
                    key={`${listing.id}_${featuredImage}`}
                    src={featuredImage}
                    columns={columns}
                    alt={listing?.title ?? 'Listing Image'}
                    onError={onImageErrorHandler}
                  />
                )}
              </Box>
            </AspectRatio>

            {/* Sold out tag */}
            {listing.status === 'sold' && (
              <Box
                position="absolute"
                top={3}
                left={3}
                px={2}
                py={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                background="red"
              >
                <Text color="white" fontWeight="bold">
                  {getSoldOutMessage(category)}
                </Text>
              </Box>
            )}

            {/* Eligible for Earning badge for eBay listings */}
            {listing.source?.toLowerCase() === 'ebay' && actionButtons === 'pendingListings' && (
              <Box
                position="absolute"
                top={'0.4rem'}
                left={'0.4rem'}
                px={{ base: 1.5, sm: 1.5, md: 2 }}
                py={{ base: 0.5, sm: 0.5, md: 1 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                background="green.500"
                borderRadius={theme.border_radius.border_radius_1}
              >
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{ base: '10px', sm: 'xs' }}
                  lineHeight="1.2"
                  whiteSpace="nowrap"
                >
                  Eligible for Earning
                </Text>
              </Box>
            )}
          </Box>
          <Stack height="full">
            <Stack height="full">
              {actionButtons && (
                <Flex alignItems="center" gap="2">
                  <Text fontSize="xs" color="grey" fontWeight="semibold">
                    {listing?.category_name?.toUpperCase()}
                  </Text>
                  <IconButton
                    aria-label="Edit Category Name"
                    icon={<EditIcon />}
                    size="xs"
                    onClick={categoryEditClickHandler}
                  />
                </Flex>
              )}
              <Text
                fontWeight="400"
                noOfLines={2}
                pb={0}
                fontFamily={theme.fonts.font_2}
                fontSize="18px"
                lineHeight="1.5"
                minHeight="54px"
                data-test="card-listing-title"
              >
                {listing.title}
              </Text>
              {(!category || category?.functional?.show_listing_price) &&
                categoryTypeHasPrice(listing?.category_type) &&
                (listing.lowest_regular_price || listing.lowest_offered_price) && (
                  <Box pb={0}>
                    <PriceTag
                      regularPrice={listing.lowest_regular_price}
                      offeredPrice={listing.lowest_offered_price}
                      currency={listing.currency}
                      regularPriceProps={{
                        fontSize: 18,
                        fontWeight: 700,
                        fontFamily: theme.fonts.font_2,
                      }}
                      offeredPriceProps={{
                        fontSize: 18,
                        fontWeight: 700,
                        fontFamily: theme.fonts.font_2,
                      }}
                    />
                  </Box>
                )}
              {category?.functional?.show_listing_description &&
                listing.source?.toLowerCase() === 'loma' && (
                  <Text
                    fontWeight="400"
                    noOfLines={2}
                    fontSize="14px"
                    fontFamily={theme.fonts.font_2}
                    data-test="card-listing-description"
                  >
                    {listing.description}
                  </Text>
                )}

              {listing?.category_type === 'auction' && (
                <Box mt="auto !important">
                  {auctionInfo?.max_bid?.max_bid_amount ? (
                    <>
                      <Text as="span" fontWeight="medium">
                        {formatPrice(auctionInfo.max_bid.max_bid_amount, {
                          currency: listing.currency,
                          alwaysCents: false,
                        })}
                      </Text>
                      <Text as="span" fontSize="sm">
                        &nbsp;&mdash; current bid
                      </Text>
                    </>
                  ) : (
                    <Text as="span" fontWeight="light">
                      no bids yet
                    </Text>
                  )}
                  <AuctionTimeLeft auctionEndStr={auctionInfo?.end} />
                </Box>
              )}
              <CategoryAttributesDisplay
                attributeValues={filterHiddenAttributes(listing?.listing_attribute_values)}
              />
              <Box pt={2}>
                {(!category || category?.functional?.show_listing_location) && location && (
                  <HStack pb={1}>
                    <MdMap />
                    <Text fontSize="12px" fontFamily={theme.fonts.font_2}>
                      {location}
                    </Text>
                  </HStack>
                )}
                {(!category || category?.functional?.show_listing_listed_at) && timeSinceListed && (
                  <HStack pb={1}>
                    <MdOutlineTimer />
                    <Text fontSize="12px" fontFamily={theme.fonts.font_2}>
                      {timeSinceListed}
                    </Text>
                  </HStack>
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </CardWrapper>
      {actionButtons && actionButtons === 'myListings' && (
        <Box mt="4">
          <ButtonGroup size="sm" w="100%" display={'flex'} flexDirection={'row'}>
            <Button
              colorScheme="primary"
              leftIcon={<Icon as={FiEdit} />}
              onClick={() => router.push(`/listing/edit/${listing.id}`)}
              width="75%"
            >
              Edit
            </Button>
            <Button
              colorScheme="red"
              onClick={() => handleDelete(listing.id)}
              isLoading={isDeleting}
            >
              <Icon as={FiTrash} />
            </Button>
          </ButtonGroup>
        </Box>
      )}
      {actionButtons && actionButtons === 'pendingListings' && (
        <Box>
          {auctionInfo?.status === 'pending_admin_approval' ? (
            <>
              <Flex>
                <Text mt={4} fontSize="0.7rem">
                  Start:{' '}
                  {auctionInfo?.startLocal ? formatDateTime(auctionInfo.startLocal) : 'Need to add'}
                </Text>
              </Flex>
              <Flex>
                <Text fontSize="0.7rem">
                  End:{' '}
                  {auctionInfo?.endLocal ? formatDateTime(auctionInfo.endLocal) : 'Need to add'}
                </Text>
              </Flex>
              <Flex>
                <Button
                  mt={4}
                  colorScheme="gray"
                  as={NextLink}
                  href={`/listing/edit/${listing.id}`}
                  target="_blank"
                  isLoading={isLoading}
                  size="sm"
                  w="full"
                >
                  Modify Auction
                </Button>
              </Flex>
            </>
          ) : (
            <Flex>
              <Button
                mt={4}
                colorScheme="blue"
                as={NextLink}
                href={`${marketplaceLink}listing/${listing.id}`}
                target="_blank"
                isLoading={isLoading}
                size="sm"
                w="full"
              >
                View
              </Button>
            </Flex>
          )}
          <Box mt="2">
            <Grid templateColumns="repeat(5, 1fr)" justifyContent="space-between" gap={1}>
              <GridItem colSpan={listing?.third_party_query_id && showSearchQueries ? 4 : 5}>
                <ButtonGroup size="sm" isAttached w="full">
                  <Button
                    colorScheme="green"
                    isLoading={isApproving}
                    onClick={() => handleApprove(listing.id)}
                    w={listing?.third_party_query_id && showSearchQueries ? '50%' : 'full'}
                    isDisabled={isDenying || (!!auctionInfo?.id && missingAuctionDates)}
                    data-test="listing-approve-btn"
                  >
                    Approve
                  </Button>
                  {listing.source === 'loma' ? (
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        colorScheme="red"
                        w={listing?.third_party_query_id && showSearchQueries ? '52%' : 'full'}
                        isLoading={isDenying}
                        isDisabled={isApproving || (!!auctionInfo?.id && missingAuctionDates)}
                        padding="6px"
                      >
                        Deny
                      </MenuButton>
                      <MenuList zIndex={4}>
                        <MenuItem onClick={() => handleDeny(listing.id)}>Deny</MenuItem>
                        <MenuItem onClick={setShowDenyMessageModal.on}>Deny with Message</MenuItem>
                      </MenuList>
                    </Menu>
                  ) : (
                    <Button
                      colorScheme="red"
                      isLoading={isDenying}
                      onClick={() => handleDeny(listing.id)}
                      w={listing?.third_party_query_id && showSearchQueries ? '50%' : 'full'}
                      isDisabled={isApproving || (!!auctionInfo?.id && missingAuctionDates)}
                    >
                      Deny
                    </Button>
                  )}
                </ButtonGroup>
              </GridItem>
              {showSearchQueries && listing?.third_party_query_id && (
                <GridItem colSpan={1}>
                  <Menu>
                    <MenuButton as={IconButton} icon={<FaChevronDown />} size="sm" />
                    <MenuList zIndex={3}>
                      <MenuItem
                        as={NextLink}
                        href={`/admin/queries/edit/${listing.third_party_query_id}`}
                      >
                        Saved Search
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </GridItem>
              )}
            </Grid>
          </Box>
        </Box>
      )}
      <DenyMessageModal
        isOpen={showDenyMessageModal}
        onClose={setShowDenyMessageModal.off}
        listingId={listing.id}
        handleDeny={handleDeny}
      />
    </Flex>
  );
};

export default memo(ListingCard);
